import { MissionResult } from './mission';

/**
 * Skills that the user can improve
 */
export interface SkillLevels {
  coordinates: number;
  basicMoves: number;
  sequences: number;
  gameAnalysis: number;
  // Additional move-related skills
  castling?: number;
  pawnPromotion?: number;
  enPassant?: number;
  pieceMoves?: PieceMoveSkills;
}

/**
 * Piece-specific move skills
 */
export interface PieceMoveSkills {
  pawn: number;
  knight: number;
  bishop: number;
  rook: number;
  queen: number;
  king: number;
}

/**
 * Move skill metrics for tracking detailed progress
 */
export interface MoveSkillMetrics {
  totalMoves: {
    attempted: number;
    successful: number;
  };
  movesByType: {
    normal: { attempted: number; successful: number };
    castling: { attempted: number; successful: number };
    enPassant: { attempted: number; successful: number };
    promotion: { attempted: number; successful: number };
  };
  movesByPiece: {
    pawn: { attempted: number; successful: number };
    knight: { attempted: number; successful: number };
    bishop: { attempted: number; successful: number };
    rook: { attempted: number; successful: number };
    queen: { attempted: number; successful: number };
    king: { attempted: number; successful: number };
  };
  averageTimePerMove: number;
  hintsUsed: number;
}

/**
 * User progress type definition
 * 
 * Defines the structure of a user's progress in the Chess Codebreaker game
 */
export interface UserProgress {
  currentStage: number;
  completedMissions: string[];
  skillLevels: SkillLevels;
  statistics: {
    totalMissionsCompleted: number;
    totalChallengesCompleted: number;
    averageAccuracy: number;
    bestScore: number;
    lastActive: string;
  };
  missionResults?: MissionResult[];
  unlockedAchievements?: string[];
  // Add move skill metrics
  moveSkillMetrics?: MoveSkillMetrics;
}

/**
 * Agent rank based on skill levels and missions completed
 * 
 * Represents the player's current rank in the spy-themed progression system
 */
export enum AgentRank {
  ROOKIE = 'Rookie Agent',
  FIELD = 'Field Agent',
  SPECIAL = 'Special Agent',
  MASTER = 'Master Spy'
}

/**
 * Get agent rank based on user progress
 * 
 * Calculates the player's rank based on their skill levels and completed missions
 * 
 * @param progress The user's progress data
 * @returns The agent rank of the player
 */
export const getAgentRank = (progress: UserProgress): AgentRank => {
  const { skillLevels, completedMissions } = progress;
  
  // Calculate the total skill level
  const totalSkill = 
    skillLevels.coordinates + 
    skillLevels.basicMoves + 
    skillLevels.sequences + 
    skillLevels.gameAnalysis;
  
  // Calculate the average skill level (out of a maximum of 10 per skill)
  const averageSkill = totalSkill / 4;
  
  // Determine rank based on average skill and missions completed
  if (averageSkill >= 8 && completedMissions.length >= 15) {
    return AgentRank.MASTER;
  } else if (averageSkill >= 5 && completedMissions.length >= 10) {
    return AgentRank.SPECIAL;
  } else if (averageSkill >= 3 && completedMissions.length >= 5) {
    return AgentRank.FIELD;
  } else {
    return AgentRank.ROOKIE;
  }
};

/**
 * Get the next rank requirements
 * 
 * Calculates what the player needs to achieve to reach the next rank
 * 
 * @param progress The user's progress data
 * @returns Requirements object for the next rank
 */
export const getNextRankRequirements = (progress: UserProgress): { 
  averageSkill: number; 
  missionsNeeded: number;
  currentRank: AgentRank;
  nextRank: AgentRank | null;
} => {
  const currentRank = getAgentRank(progress);
  const { skillLevels, completedMissions } = progress;
  
  // Calculate current average skill
  const totalSkill = 
    skillLevels.coordinates + 
    skillLevels.basicMoves + 
    skillLevels.sequences + 
    skillLevels.gameAnalysis;
  
  const averageSkill = totalSkill / 4;
  
  // Define requirements based on current rank
  switch (currentRank) {
    case AgentRank.ROOKIE:
      return {
        averageSkill: 3,
        missionsNeeded: Math.max(0, 5 - completedMissions.length),
        currentRank,
        nextRank: AgentRank.FIELD
      };
    case AgentRank.FIELD:
      return {
        averageSkill: 5,
        missionsNeeded: Math.max(0, 10 - completedMissions.length),
        currentRank,
        nextRank: AgentRank.SPECIAL
      };
    case AgentRank.SPECIAL:
      return {
        averageSkill: 8,
        missionsNeeded: Math.max(0, 15 - completedMissions.length),
        currentRank,
        nextRank: AgentRank.MASTER
      };
    case AgentRank.MASTER:
      return {
        averageSkill: 10,
        missionsNeeded: 0,
        currentRank,
        nextRank: null // Already at max rank
      };
  }
}; 